import { ExpandableImage } from '@/components/expandable-image';
import { MobileContainer } from '@/components/workflow/mobile-container';
import { MobileHeader } from '@/components/workflow/mobile-header';
import {
	Button,
	Flex,
	NumberInput,
	Slider,
	Space,
	Switch,
	Text,
	Textarea,
	Title,
} from '@mantine/core';
import NumberFlow from '@number-flow/react';
import { IconPhotoPlus } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback } from 'react';
import { useBagAuditContext } from '../hook';
import styles from '../styles.module.css';

export const BagAuditFormPage = () => {
	const { setPage, evaluatingBag, setEvaluatingBag } = useBagAuditContext();

	const updateBagData = useCallback(
		(updates: Partial<typeof evaluatingBag>) => {
			setEvaluatingBag((prev) => (prev ? { ...prev, ...updates } : null));
		},
		[setEvaluatingBag]
	);

	const handleContaminationToggle = useCallback(() => {
		updateBagData({
			isContaminated: !evaluatingBag?.isContaminated,
			contamination: !evaluatingBag?.isContaminated ? evaluatingBag?.contamination || 0 : 0,
			contaminationDetails: !evaluatingBag?.isContaminated
				? evaluatingBag?.contaminationDetails || ''
				: '',
		});
	}, [
		evaluatingBag?.isContaminated,
		evaluatingBag?.contamination,
		evaluatingBag?.contaminationDetails,
		updateBagData,
	]);

	const handleDamageToggle = useCallback(() => {
		updateBagData({ isDamaged: !evaluatingBag?.isDamaged });
	}, [evaluatingBag?.isDamaged, updateBagData]);

	const handleContaminationChange = useCallback(
		(value: number) => {
			updateBagData({ contamination: value });
		},
		[updateBagData]
	);

	const handleDetailsChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			updateBagData({ contaminationDetails: e.target.value });
		},
		[updateBagData]
	);

	const handlePhotoDelete = useCallback(
		(index: number) => {
			if (evaluatingBag) {
				updateBagData({
					photos: evaluatingBag.photos.filter((_, i) => i !== index),
				});
			}
		},
		[evaluatingBag, updateBagData]
	);

	return (
		<MobileContainer header={<MobileHeader title="Bag Audit" onBack={() => setPage('SCAN')} />}>
			{evaluatingBag && (
				<Flex p="md" gap="lg" direction="column">
					<Flex
						style={{ borderBottom: '1px solid #E0E0E0', paddingBottom: '22px' }}
						direction="column"
						gap="md"
					>
						<Title order={3}>Photos</Title>
						<Flex className={styles.horizontalScrollContainer}>
							{evaluatingBag.photos.length > 0 ? (
								evaluatingBag.photos.map((photo, i) => (
									<Flex key={i} className={styles.photoCard}>
										<ExpandableImage
											src={photo}
											id={`bag-audit-photo-${i}`}
											onDelete={() => handlePhotoDelete(i)}
										/>
									</Flex>
								))
							) : (
								<Flex
									style={{ border: '1px dashed var(--mantine-color-gray-6)', borderRadius: '6px' }}
									w="100%"
									p="md"
									direction="column"
									c="gray"
									justify="center"
									align="center"
									gap="sm"
								>
									<IconPhotoPlus size={48} />
									<Text size="md">Add load photos</Text>
								</Flex>
							)}
						</Flex>
						<Button bg="#C7E8E9" size="md" variant="filled" onClick={() => setPage('PHOTOS')}>
							Add Photos
						</Button>
					</Flex>
					<Flex direction="column" gap="md">
						<Title order={3}>Details</Title>
						<Flex direction="row" justify="space-between" align="center" gap="md">
							<Text size="sm" span fw={500}>
								Bag Weight:
							</Text>
							<NumberInput
								size="md"
								label="Bag Weight"
								value={evaluatingBag?.weight || ''}
								onChange={(value) => {
									const numValue =
										typeof value === 'string' ? parseFloat(value || '0') : value || 0;
									if (typeof numValue === 'number') {
										updateBagData({ weight: numValue });
									}
								}}
								placeholder="0"
								rightSection={
									<Text size="sm" c="var(--mantine-color-grey-5)" pr="xl">
										KG
									</Text>
								}
								w="100%"
							/>
						</Flex>
						<Flex direction="row" justify="space-between" align="center" pb="xs" gap="md">
							<Text size="sm" span fw={500}>
								Contaminated
							</Text>
							<Switch
								size="lg"
								checked={evaluatingBag?.isContaminated || false}
								onChange={handleContaminationToggle}
								color="green"
							/>
						</Flex>
						<Flex direction="row" justify="space-between" align="center" gap="md">
							<Text size="sm" span fw={500}>
								Bag is damaged
							</Text>
							<Switch
								checked={evaluatingBag?.isDamaged || false}
								size="lg"
								onChange={handleDamageToggle}
								color="green"
							/>
						</Flex>
						<AnimatePresence>
							{evaluatingBag?.isContaminated && (
								<motion.div
									initial={{ opacity: 0, height: 0 }}
									animate={{ opacity: 1, height: 'auto' }}
									exit={{ opacity: 0, height: 0 }}
									transition={{ duration: 0.3, ease: 'easeInOut' }}
									style={{ overflow: 'hidden' }}
								>
									<Flex direction="column" gap="md">
										<Flex direction="row" justify="space-between" align="center" gap="md">
											<Text size="sm" span fw={500}>
												Amount
											</Text>
											<Text size="lg" mt={-5} fw={600}>
												<NumberFlow value={evaluatingBag?.contamination} suffix="%" />
											</Text>
										</Flex>
										<Slider
											color="blue"
											marks={[
												{ value: 20, label: '20%' },
												{ value: 50, label: '50%' },
												{ value: 80, label: '80%' },
											]}
											label={null}
											onChange={handleContaminationChange}
											value={evaluatingBag?.contamination || 0}
											step={5}
											min={0}
											max={100}
											thumbSize={28}
											px="md"
										/>
										<Space h="sm" />
										<Textarea
											label="Contamination Details"
											placeholder="Describe the type of contamination found..."
											value={evaluatingBag?.contaminationDetails || ''}
											onChange={handleDetailsChange}
											minRows={3}
											maxRows={5}
											autosize
											maxLength={500}
										/>
									</Flex>
								</motion.div>
							)}
						</AnimatePresence>
						<Space h="sm" />
					</Flex>
				</Flex>
			)}
		</MobileContainer>
	);
};
